import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { DisplayMode, RealEstate, RealEstateCategory, RealEstateSettings, RealEstateTag } from '../types/RealEstate';

interface LoadingState {
  initialize: boolean;
  settings: boolean;
  properties: boolean;
  categories: boolean;
  tags: boolean;
  import: boolean;
}

interface RealEstateStore {
  properties: RealEstate[];
  categories: RealEstateCategory[];
  tags: RealEstateTag[];
  settings: RealEstateSettings;
  loading: LoadingState;
  error: string | null;
  initialized: boolean;
  initialize: (chatbotPublicId: string, callerLoading?: keyof LoadingState) => Promise<void>;
  addProperty: (chatbotPublicId: string, property: Omit<RealEstate, 'id' | 'position' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateProperty: (chatbotPublicId: string, propertyId: string, updates: Partial<RealEstate>) => Promise<void>;
  deleteProperty: (chatbotPublicId: string, propertyId: string) => Promise<void>;
  reorderProperties: (chatbotPublicId: string, propertyIds: string[]) => Promise<void>;
  addCategory: (chatbotPublicId: string, category: Omit<RealEstateCategory, 'id' | 'position'>) => Promise<void>;
  updateCategory: (chatbotPublicId: string, categoryId: string, updates: Partial<RealEstateCategory>) => Promise<void>;
  deleteCategory: (chatbotPublicId: string, categoryId: string) => Promise<void>;
  addTag: (chatbotPublicId: string, name: string) => Promise<void>;
  updateTag: (chatbotPublicId: string, tagId: string, name: string) => Promise<void>;
  deleteTag: (chatbotPublicId: string, tagId: string) => Promise<void>;
  importFromCSV: (chatbotPublicId: string, properties: Omit<RealEstate, 'id' | 'position' | 'created_at' | 'updated_at'>[]) => Promise<void>;
  updateSettings: (chatbotPublicId: string, settings: Partial<RealEstateSettings>) => Promise<void>;
  reorderCategories: (chatbotPublicId: string, categoryIds: string[]) => Promise<void>;
}

const DEFAULT_SETTINGS: RealEstateSettings = {
  displayMode: 'grid',
  gridColumns: 3,
  spacing: 4,
  showPrices: true,
  showSurface: true,
  showRooms: true,
  showRoi: true,
  showResale: true,
  currencyDisplay: '€'
};

export const useRealEstateSystem = create<RealEstateStore>((set, get) => ({
  properties: [],
  categories: [],
  tags: [],
  settings: DEFAULT_SETTINGS,
  loading: {
    initialize: false,
    settings: false,
    properties: false,
    categories: false,
    tags: false,
    import: false
  },
  error: null,
  initialized: false,

  initialize: async (chatbotPublicId, callerLoading) => {
    // If callerLoading is provided, this was called from another method
    const isDirectCall = !callerLoading;
    
    try {
      // Only set loading.initialize if this is a direct call, not from another method
      if (isDirectCall) {
        set({ 
          loading: { ...get().loading, initialize: true },
          error: null 
        });
      }

      // Fetch settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('realestate_settings')
        .select('*')
        .eq('chatbot_public_id', chatbotPublicId)
        .single();

      if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;

      // Fetch properties
      const { data: propertiesData, error: propertiesError } = await supabase
        .from('realestate_properties')
        .select('*')
        .eq('chatbot_public_id', chatbotPublicId)
        .order('position');

      if (propertiesError) throw propertiesError;

      // Fetch categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('realestate_categories')
        .select('*')
        .eq('chatbot_public_id', chatbotPublicId)
        .order('position');

      if (categoriesError) throw categoriesError;

      // Fetch tags
      const { data: tagsData, error: tagsError } = await supabase
        .from('realestate_tags')
        .select('*')
        .eq('chatbot_public_id', chatbotPublicId)
        .order('name');

      if (tagsError) throw tagsError;

      // Update state, but preserve loading states
      const currentLoadingState = get().loading;
      set({
        settings: settingsData ? {
          displayMode: (settingsData.display_mode || 'grid') as DisplayMode,
          gridColumns: settingsData.grid_columns || 3,
          spacing: settingsData.spacing || 4,
          showPrices: settingsData.show_prices !== null ? settingsData.show_prices : true,
          showSurface: settingsData.show_surface !== null ? settingsData.show_surface : true,
          showRooms: settingsData.show_rooms !== null ? settingsData.show_rooms : true,
          showRoi: settingsData.show_roi !== null ? settingsData.show_roi : true,
          showResale: settingsData.show_resale !== null ? settingsData.show_resale : true,
          currencyDisplay: settingsData.currency_display || '€'
        } : DEFAULT_SETTINGS,
        properties: (propertiesData || []) as unknown as RealEstate[],
        categories: (categoriesData || []) as unknown as RealEstateCategory[],
        tags: (tagsData || []) as unknown as RealEstateTag[],
        initialized: true,
        loading: {
          ...currentLoadingState,
          initialize: isDirectCall ? false : currentLoadingState.initialize
        }
      });
    } catch (err) {
      console.error('Error initializing real estate:', err);
      // Only update error if this is a direct call
      if (isDirectCall) {
        set({ 
          error: err instanceof Error ? err.message : 'Error initializing real estate',
          settings: DEFAULT_SETTINGS,
          properties: [],
          categories: [],
          tags: [],
          initialized: true,
          loading: { ...get().loading, initialize: false }
        });
      } else {
        // If called from another method, don't update anything except the error
        set({ 
          error: err instanceof Error ? err.message : 'Error initializing real estate'
        });
      }
    }
  },

  addProperty: async (chatbotPublicId, property) => {
    try {
      set({ 
        loading: { ...get().loading, properties: true },
        error: null 
      });

      // Get current max position
      const maxPosition = Math.max(0, ...get().properties.map(p => p.position));

      const { error } = await supabase
        .from('realestate_properties')
        .insert({
          chatbot_public_id: chatbotPublicId,
          ...property,
          position: maxPosition + 1
        });

      if (error) throw error;

      // Pass 'properties' as the caller loading state key
      await get().initialize(chatbotPublicId, 'properties');
      
      // Reset loading state
      set({ loading: { ...get().loading, properties: false } });
    } catch (err) {
      console.error('Error adding property:', err);
      set({ 
        error: err instanceof Error ? err.message : 'Error adding property',
        loading: { ...get().loading, properties: false }
      });
    }
  },

  updateProperty: async (chatbotPublicId, propertyId, updates) => {
    try {
      set({ loading: { ...get().loading, properties: true }, error: null });

      const { error } = await supabase
        .from('realestate_properties')
        .update(updates)
        .eq('id', propertyId)
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      // Pass 'properties' as the caller loading state key
      await get().initialize(chatbotPublicId, 'properties');
      
      set({ loading: { ...get().loading, properties: false } });
    } catch (err) {
      console.error('Error updating property:', err);
      set({ 
        error: err instanceof Error ? err.message : 'Error updating property',
        loading: { ...get().loading, properties: false }
      });
    }
  },

  deleteProperty: async (chatbotPublicId, propertyId) => {
    try {
      set({ loading: { ...get().loading, properties: true }, error: null });

      const { error } = await supabase
        .from('realestate_properties')
        .delete()
        .eq('id', propertyId)
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      set(state => ({
        properties: state.properties.filter(p => p.id !== propertyId),
        loading: { ...state.loading, properties: false }
      }));
    } catch (err) {
      console.error('Error deleting property:', err);
      set({ 
        error: err instanceof Error ? err.message : 'Error deleting property',
        loading: { ...get().loading, properties: false } 
      });
    }
  },

  reorderProperties: async (chatbotPublicId, propertyIds) => {
    try {
      set({ loading: { ...get().loading, properties: true }, error: null });

      // Update positions
      for (let i = 0; i < propertyIds.length; i++) {
        const { error } = await supabase
          .from('realestate_properties')
          .update({ position: i })
          .eq('id', propertyIds[i])
          .eq('chatbot_public_id', chatbotPublicId);

        if (error) throw error;
      }

      // Pass 'properties' as the caller loading state key
      await get().initialize(chatbotPublicId, 'properties');
      
      set({ loading: { ...get().loading, properties: false } });
    } catch (err) {
      console.error('Error reordering properties:', err);
      set({ 
        error: err instanceof Error ? err.message : 'Error reordering properties',
        loading: { ...get().loading, properties: false }
      });
    }
  },

  addCategory: async (chatbotPublicId, category) => {
    try {
      set({ loading: { ...get().loading, categories: true }, error: null });

      // Get current max position
      const maxPosition = Math.max(0, ...get().categories.map(c => c.position));

      const { error } = await supabase
        .from('realestate_categories')
        .insert({
          chatbot_public_id: chatbotPublicId,
          ...category,
          position: maxPosition + 1
        });

      if (error) throw error;

      // Pass 'categories' as the caller loading state key
      await get().initialize(chatbotPublicId, 'categories');
      
      set({ loading: { ...get().loading, categories: false } });
    } catch (err) {
      console.error('Error adding category:', err);
      set({ 
        error: err instanceof Error ? err.message : 'Error adding category',
        loading: { ...get().loading, categories: false }
      });
    }
  },

  updateCategory: async (chatbotPublicId, categoryId, updates) => {
    try {
      set({ loading: { ...get().loading, categories: true }, error: null });

      const { error } = await supabase
        .from('realestate_categories')
        .update(updates)
        .eq('id', categoryId)
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      // Pass 'categories' as the caller loading state key
      await get().initialize(chatbotPublicId, 'categories');
      
      set({ loading: { ...get().loading, categories: false } });
    } catch (err) {
      console.error('Error updating category:', err);
      set({ 
        error: err instanceof Error ? err.message : 'Error updating category',
        loading: { ...get().loading, categories: false }
      });
    }
  },

  deleteCategory: async (chatbotPublicId, categoryId) => {
    try {
      set({ loading: { ...get().loading, categories: true }, error: null });

      // First, update properties that have this category to remove it
      const { data: properties } = await supabase
        .from('realestate_properties')
        .select('id, category_id')
        .eq('chatbot_public_id', chatbotPublicId);

      if (properties) {
        for (const property of properties) {
          if (property.category_id === categoryId) {
            await supabase
              .from('realestate_properties')
              .update({ category_id: null })
              .eq('id', property.id)
              .eq('chatbot_public_id', chatbotPublicId);
          }
        }
      }

      // Then delete the category
      const { error } = await supabase
        .from('realestate_categories')
        .delete()
        .eq('id', categoryId)
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      // Pass 'categories' as the caller loading state key
      await get().initialize(chatbotPublicId, 'categories');
      
      set({ loading: { ...get().loading, categories: false } });
    } catch (err) {
      console.error('Error deleting category:', err);
      set({ 
        error: err instanceof Error ? err.message : 'Error deleting category',
        loading: { ...get().loading, categories: false }
      });
    }
  },

  addTag: async (chatbotPublicId, name) => {
    try {
      set({ loading: { ...get().loading, tags: true }, error: null });

      const { error } = await supabase
        .from('realestate_tags')
        .insert({
          chatbot_public_id: chatbotPublicId,
          name
        });

      if (error) throw error;

      // Pass 'tags' as the caller loading state key
      await get().initialize(chatbotPublicId, 'tags');
      
      set({ loading: { ...get().loading, tags: false } });
    } catch (err) {
      console.error('Error adding tag:', err);
      set({ 
        error: err instanceof Error ? err.message : 'Error adding tag',
        loading: { ...get().loading, tags: false }
      });
    }
  },

  updateTag: async (chatbotPublicId, tagId, name) => {
    try {
      set({ loading: { ...get().loading, tags: true }, error: null });

      const { error } = await supabase
        .from('realestate_tags')
        .update({ name })
        .eq('id', tagId)
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      // Pass 'tags' as the caller loading state key
      await get().initialize(chatbotPublicId, 'tags');
      
      set({ loading: { ...get().loading, tags: false } });
    } catch (err) {
      console.error('Error updating tag:', err);
      set({ 
        error: err instanceof Error ? err.message : 'Error updating tag',
        loading: { ...get().loading, tags: false }
      });
    }
  },

  deleteTag: async (chatbotPublicId, tagId) => {
    try {
      set({ loading: { ...get().loading, tags: true }, error: null });

      // First, update properties to remove this tag from their tags array
      const { data: properties } = await supabase
        .from('realestate_properties')
        .select('id, tags')
        .eq('chatbot_public_id', chatbotPublicId);

      if (properties) {
        for (const property of properties) {
          if (property.tags && property.tags.includes(tagId)) {
            await supabase
              .from('realestate_properties')
              .update({
                tags: property.tags.filter((id: string) => id !== tagId)
              })
              .eq('id', property.id)
              .eq('chatbot_public_id', chatbotPublicId);
          }
        }
      }

      // Then delete the tag
      const { error } = await supabase
        .from('realestate_tags')
        .delete()
        .eq('id', tagId)
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      // Pass 'tags' as the caller loading state key
      await get().initialize(chatbotPublicId, 'tags');
      
      set({ loading: { ...get().loading, tags: false } });
    } catch (err) {
      console.error('Error deleting tag:', err);
      set({ 
        error: err instanceof Error ? err.message : 'Error deleting tag',
        loading: { ...get().loading, tags: false }
      });
    }
  },

  importFromCSV: async (chatbotPublicId: string, properties: Omit<RealEstate, 'id' | 'position' | 'created_at' | 'updated_at'>[]): Promise<void> => {
    try {
      set({ loading: { ...get().loading, import: true }, error: null });

      // Get existing categories for this chatbot
      const { data: existingCategories } = await supabase
        .from('realestate_categories')
        .select('id, name')
        .eq('chatbot_public_id', chatbotPublicId);
      
      const categoryMap = new Map();
      if (existingCategories) {
        existingCategories.forEach(cat => {
          categoryMap.set(cat.name.toLowerCase(), cat.id);
        });
      }

      // Get existing tags for this chatbot
      const { data: existingTags } = await supabase
        .from('realestate_tags')
        .select('id, name')
        .eq('chatbot_public_id', chatbotPublicId);
      
      const tagMap = new Map();
      if (existingTags) {
        existingTags.forEach(tag => {
          tagMap.set(tag.name.toLowerCase(), tag.id);
        });
      }

      // For each property in the CSV
      for (const property of properties) {
        // Handle category: convert category name to category_id
        let categoryId: string | null = null;
        
        if (property.category && typeof property.category === 'string') {
          const categoryNameLower = property.category.toLowerCase();
          
          // Check if the category already exists
          if (categoryMap.has(categoryNameLower)) {
            categoryId = categoryMap.get(categoryNameLower);
          } else {
            // Create a new category
            const { data: newCategory, error } = await supabase
              .from('realestate_categories')
              .insert({
                chatbot_public_id: chatbotPublicId,
                name: property.category
              })
              .select('id')
              .single();
            
            if (error) {
              console.error('Error creating category:', error);
            } else if (newCategory) {
              categoryId = newCategory.id;
              categoryMap.set(categoryNameLower, newCategory.id);
            }
          }
        }
        
        // Handle tags: convert tag names to tag UUIDs
        const tagIds: string[] = [];
        
        if (property.tags && Array.isArray(property.tags) && property.tags.length > 0) {
          // For each tag name in the property
          for (const tagName of property.tags) {
            const tagNameLower = tagName.toLowerCase();
            // Check if the tag already exists
            if (tagMap.has(tagNameLower)) {
              tagIds.push(tagMap.get(tagNameLower));
            } else {
              // Create a new tag
              const { data: newTag, error } = await supabase
                .from('realestate_tags')
                .insert({
                  chatbot_public_id: chatbotPublicId,
                  name: tagName
                })
                .select('id')
                .single();
              
              if (error) {
                console.error('Error creating tag:', error);
                continue;
              }
              
              if (newTag) {
                tagIds.push(newTag.id);
                tagMap.set(tagNameLower, newTag.id);
              }
            }
          }
        }
        
        // Prepare the property with processed tags, category, and chatbot_public_id
        const propertyToInsert = {
          ...property,
          category_id: categoryId,
          tags: tagIds.length > 0 ? tagIds : null,
          chatbot_public_id: chatbotPublicId
        };
        
        // Insert the property
        const { error } = await supabase
          .from('realestate_properties')
          .insert(propertyToInsert);
          
        if (error) {
          console.error('Error importing property:', error);
          set({ 
            error: `Error importing properties: ${error.message}`,
            loading: { ...get().loading, import: false }
          });
          return;
        }
      }

      // Refresh the data without changing the import loading state
      await get().initialize(chatbotPublicId, 'import');
      
      set({
        loading: { ...get().loading, import: false }
      });
    } catch (err) {
      console.error('Error in importFromCSV:', err);
      set({ 
        error: `Error importing properties: ${err instanceof Error ? err.message : String(err)}`,
        loading: { ...get().loading, import: false }
      });
    }
  },

  updateSettings: async (chatbotPublicId, settings) => {
    try {
      set({ loading: { ...get().loading, settings: true }, error: null });

      const { data, error: checkError } = await supabase
        .from('realestate_settings')
        .select('id')
        .eq('chatbot_public_id', chatbotPublicId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') throw checkError;

      // Convert from camelCase to snake_case for database
      const dbSettings = {
        ...(settings.displayMode !== undefined && { display_mode: settings.displayMode }),
        ...(settings.gridColumns !== undefined && { grid_columns: settings.gridColumns }),
        ...(settings.spacing !== undefined && { spacing: settings.spacing }),
        ...(settings.showPrices !== undefined && { show_prices: settings.showPrices }),
        ...(settings.showSurface !== undefined && { show_surface: settings.showSurface }),
        ...(settings.showRooms !== undefined && { show_rooms: settings.showRooms }),
        ...(settings.showRoi !== undefined && { show_roi: settings.showRoi }),
        ...(settings.showResale !== undefined && { show_resale: settings.showResale }),
        ...(settings.currencyDisplay !== undefined && { currency_display: settings.currencyDisplay })
      };

      if (data) {
        // Update existing settings
        const { error } = await supabase
          .from('realestate_settings')
          .update(dbSettings)
          .eq('id', data.id);

        if (error) throw error;
      } else {
        // Insert new settings
        const { error } = await supabase
          .from('realestate_settings')
          .insert({
            chatbot_public_id: chatbotPublicId,
            ...dbSettings
          });

        if (error) throw error;
      }

      // First update local state with new settings
      set(state => ({
        settings: {
          ...state.settings,
          ...settings
        }
      }));

      // Pass 'settings' as the caller loading state key
      await get().initialize(chatbotPublicId, 'settings');
      
      set({ loading: { ...get().loading, settings: false } });
    } catch (err) {
      console.error('Error updating settings:', err);
      set({ 
        error: err instanceof Error ? err.message : 'Error updating settings',
        loading: { ...get().loading, settings: false }
      });
    }
  },

  reorderCategories: async (chatbotPublicId, categoryIds) => {
    try {
      set({ loading: { ...get().loading, categories: true }, error: null });

      // Update positions
      for (let i = 0; i < categoryIds.length; i++) {
        const { error } = await supabase
          .from('realestate_categories')
          .update({ position: i })
          .eq('id', categoryIds[i])
          .eq('chatbot_public_id', chatbotPublicId);

        if (error) throw error;
      }

      // Pass 'categories' as the caller loading state key
      await get().initialize(chatbotPublicId, 'categories');
      
      set({ loading: { ...get().loading, categories: false } });
    } catch (err) {
      console.error('Error reordering categories:', err);
      set({ 
        error: err instanceof Error ? err.message : 'Error reordering categories',
        loading: { ...get().loading, categories: false }
      });
    }
  }
}));